/**
 * RdRepo sobre Supabase. Misma lógica que DexieRdRepo.
 * `upsertDia` y `changeState` no usan transacción atómica (read-then-write);
 * suficiente para el piloto (la concurrencia sobre el mismo RD del mismo día
 * es benigna porque solo el contratista de esa SO lo edita).
 */

import type { RdRepo } from "../../repos/RdRepo";
import type { Rd } from "@app-types/entities";
import type { EstadoRd } from "@constants/estados";
import { TRANSICIONES_RD, puedeTransicionar } from "@constants/estados";
import { sha256 } from "@/lib/crypto";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";
import { ValidationError } from "../../repos/BaseRepo";
import { puedeAprobarRdPorOrden, mensajeBloqueoPorOrden } from "@/features/rd/rdRules";

export class SupabaseRdRepo extends SupabaseBaseRepo<Rd> implements RdRepo {
  constructor(tenantId: string) {
    super("rd", tenantId);
  }

  async list(filter?: Partial<Rd>): Promise<Rd[]> {
    const all = await this.listAll();
    if (filter?.estado) return all.filter((r) => r.estado === filter.estado);
    if (filter?.soId) return all.filter((r) => r.soId === filter.soId);
    return all.sort((a, b) => b.fecha.localeCompare(a.fecha));
  }

  async listBySo(soId: string): Promise<Rd[]> {
    return (await this.listAll())
      .filter((r) => r.soId === soId)
      .sort((a, b) => b.fecha.localeCompare(a.fecha));
  }

  async listByEstado(estado: EstadoRd): Promise<Rd[]> {
    return (await this.listAll()).filter((r) => r.estado === estado);
  }

  async listByRangoFechas(desde: string, hasta: string): Promise<Rd[]> {
    return (await this.listAll()).filter((r) => r.fecha >= desde && r.fecha <= hasta);
  }

  async existsForSoAndDate(soId: string, fecha: string): Promise<boolean> {
    return (await this.listAll()).some((r) => r.soId === soId && r.fecha === fecha);
  }

  async upsertDia(
    soId: string,
    fecha: string,
    patch: Partial<Pick<Rd, "jornada" | "recursos" | "avancesTareas" | "eventos" | "fotos">>,
    createDefaults: Pick<Rd, "jornada" | "recursos" | "avancesTareas" | "eventos">,
    userEmail: string,
  ): Promise<string> {
    const existing = (await this.listAll()).find((r) => r.soId === soId && r.fecha === fecha);
    const now = new Date().toISOString();
    if (existing) {
      if (existing.estado !== "BORRADOR") {
        throw new Error("El reporte diario ya fue enviado a revisión; no admite cambios.");
      }
      await this.putRaw({
        ...existing,
        ...patch,
        version: existing.version + 1,
        updatedAt: now,
        updatedBy: userEmail,
      });
      return existing.id;
    }
    const nuevo: Rd = {
      ...createDefaults,
      ...patch,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      soId,
      fecha,
      estado: "BORRADOR",
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async get(id: string): Promise<Rd | null> {
    return this.getById(id);
  }

  async create(data: Omit<Rd, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    if (await this.existsForSoAndDate(data.soId, data.fecha)) {
      throw new Error(`Ya existe un RD para la SO ${data.soId} en la fecha ${data.fecha}`);
    }
    const now = new Date().toISOString();
    const nuevo: Rd = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(id: string, data: Partial<Rd>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`RD ${id} not found`);
    const updated: Rd = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async changeState(id: string, nextState: EstadoRd, userEmail: string, observaciones?: string): Promise<void> {
    const rd = await this.get(id);
    if (!rd) throw new Error(`RD ${id} not found`);
    if (rd.estado === nextState) return;
    if (!puedeTransicionar(rd.estado, nextState, TRANSICIONES_RD)) {
      throw new Error(`Transicion ${rd.estado} -> ${nextState} no permitida`);
    }

    // Regla de orden cronologico del responsable del PMIS: no se aprueba un
    // reporte si queda otro anterior de la misma OT sin decidir. Vive aqui y no
    // solo en la interfaz porque es un invariante de datos y hay DOS
    // disparadores de aprobacion (RdDetailPage y SoCotizacionPage); el repo es
    // su unico punto de convergencia.
    if (nextState === "APROBADO") {
      const { puede, bloqueantes } = puedeAprobarRdPorOrden(rd, await this.listBySo(rd.soId));
      if (!puede) throw new ValidationError(mensajeBloqueoPorOrden(bloqueantes));
    }
    const now = new Date().toISOString();
    const updates: Partial<Rd> = { estado: nextState };
    const nuevaEntrada = {
      fecha: now,
      estadoAnterior: rd.estado,
      estadoNuevo: nextState,
      usuario: userEmail,
      ...(observaciones !== undefined && observaciones !== "" && { observaciones }),
    };
    updates.historial = [...(rd.historial ?? []), nuevaEntrada];
    if (nextState === "EN_REVISION") {
      updates.nivelRevision = rd.nivelRevision ?? "INT";
    }
    if (nextState === "BORRADOR") {
      // Rechazo del PL2 o re-edición habilitada sobre un RD aprobado (pt. 12-13).
      (updates as Record<string, unknown>).nivelRevision = undefined;
      if (observaciones) updates.observacionesPl2 = observaciones;
      if (rd.estado === "APROBADO") {
        // Limpiar integración: al re-aprobar, los valores se recalculan sin doble carga.
        (updates as Record<string, unknown>).firmaSha256 = undefined;
        (updates as Record<string, unknown>).fechaIntegracion = undefined;
        (updates as Record<string, unknown>).aprobadoPorPl2 = undefined;
      }
    }
    if (nextState === "APROBADO") {
      updates.aprobadoPorPl2 = userEmail;
      if (observaciones) updates.observacionesPl2 = observaciones;
      updates.fechaIntegracion = now;
      (updates as Record<string, unknown>).nivelRevision = undefined;
      updates.firmaSha256 = await this.calculateSignature(id);
    }
    await this.update(id, updates, rd.version);
  }

  async updateSubFlags(
    id: string,
    flags: { nivelRevision?: "INT" | "PL" | null; calificacionInt?: number; observacionesInt?: string },
    userEmail: string,
  ): Promise<void> {
    const rd = await this.get(id);
    if (!rd) throw new Error(`RD ${id} not found`);
    const cambios: Partial<Rd> = {};
    let descripcion = "";
    if (flags.nivelRevision !== undefined) {
      if (flags.nivelRevision === null) (cambios as Record<string, unknown>).nivelRevision = undefined;
      else cambios.nivelRevision = flags.nivelRevision;
      descripcion =
        flags.nivelRevision === "PL" ? "INT aprobó y escaló al PL"
          : flags.nivelRevision === "INT" ? "Revisión devuelta al INT"
          : "Nivel de revisión limpiado";
    }
    if (flags.calificacionInt !== undefined) {
      cambios.calificacionInt = flags.calificacionInt;
      descripcion = descripcion ? `${descripcion} · calificación INT ${flags.calificacionInt}★` : `Calificación INT ${flags.calificacionInt}★`;
    }
    if (flags.observacionesInt !== undefined) {
      cambios.observacionesInt = flags.observacionesInt;
      cambios.aprobadoPorInt = userEmail;
    }
    if (Object.keys(cambios).length === 0) return;
    const entry = {
      fecha: new Date().toISOString(),
      estadoAnterior: rd.estado,
      estadoNuevo: rd.estado,
      usuario: userEmail || "system",
      observaciones: descripcion,
    };
    cambios.historial = [...(rd.historial ?? []), entry];
    await this.update(id, cambios, rd.version);
  }

  async calculateSignature(id: string): Promise<string> {
    const rd = await this.get(id);
    if (!rd) throw new Error(`RD ${id} not found`);
    const payload = {
      soId: rd.soId,
      fecha: rd.fecha,
      jornada: rd.jornada,
      avancesTareas: rd.avancesTareas,
      recursos: rd.recursos,
    };
    return sha256(JSON.stringify(payload));
  }
}
